import { Button as AriaButton } from 'react-aria-components';
import { cn } from '../lib/cn';

/**
 * Switching between views or tabs (Kalender: Tag · Woche · Monat · Jahr, Essen: Heute · Woche …).
 * Mike, 19.10.: "eine neue Tab-Komponente, die als solche erkennbar ist und sich auch von einem
 * normalen Button leicht unterscheidet … und nicht diese Kontrastprobleme aufweist". A quiet track,
 * the chosen part lifted with a coloured line underneath – own tokens (`segment-*`), checked for
 * contrast in light, dark and the wall at night.
 */
export const segmentTrack =
  'flex gap-0.5 rounded-control border border-border-default bg-segment-track p-0.5';

export const segmentItem = (selected: boolean) =>
  cn(
    'relative flex min-h-touch-min min-w-0 flex-1 cursor-pointer items-center justify-center rounded-control px-inset-xs text-sm outline-none sm:px-inset-sm',
    'data-focus-visible:outline-3 data-focus-visible:outline-border-focus focus-visible:outline-3 focus-visible:outline-border-focus',
    selected
      ? cn(
          'bg-segment-selected font-black text-segment-on-selected shadow-raised',
          // the line under the chosen part: a switch, not a button
          'after:absolute after:inset-x-3 after:bottom-1 after:h-0.5 after:rounded-chip after:bg-segment-indicator',
        )
      : 'font-semibold text-segment-text data-hovered:bg-action-quiet-hover hover:bg-action-quiet-hover',
  );

export interface SegmentedControlProps<T extends string> {
  /** Name of the group for screen readers, e.g. "Ansicht". */
  label: string;
  options: readonly { value: T; label: string }[];
  value: T | null;
  onChange: (value: T) => void;
  className?: string;
}

export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div role="group" aria-label={label} className={cn(segmentTrack, className)}>
      {options.map((o) => (
        <AriaButton
          key={o.value}
          aria-pressed={o.value === value}
          onPress={() => onChange(o.value)}
          className={segmentItem(o.value === value)}
        >
          {o.label}
        </AriaButton>
      ))}
    </div>
  );
}
